import { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  RotateCcw,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Select } from "antd";
import { PtsProjectService } from "@/services/APIs";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../../components/ui/collapsible";
import { Label } from "../../../components/ui/label";
import { INVOICE_STATUS_OPTIONS } from "./types";

const FILTER_DATE_INPUT_CLASS =
  "h-9 w-full rounded-lg border border-gray-200 bg-[#fafafa] px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20";
const FILTER_SELECT_CLASS =
  "w-full [&_.ant-select-selector]:!h-9 [&_.ant-select-selector]:!min-h-9 [&_.ant-select-selector]:!items-center [&_.ant-select-selector]:!rounded-lg [&_.ant-select-selector]:!border-gray-200 [&_.ant-select-selector]:!bg-[#fafafa] [&_.ant-select-selector]:!px-3 [&_.ant-select-selection-item]:!leading-7 [&_.ant-select-selection-placeholder]:!leading-7";

export interface BillingInvoiceFilterValues {
  startDate: string;
  endDate: string;
  projectId: string | undefined;
  status: string | undefined;
}

interface BillingInvoiceFiltersProps {
  values: BillingInvoiceFilterValues;
  onChange: (values: BillingInvoiceFilterValues) => void;
  idPrefix?: string;
  statusOptions?: readonly string[];
}

const BillingInvoiceFilters = ({
  values,
  onChange,
  idPrefix = "billing",
  statusOptions = INVOICE_STATUS_OPTIONS,
}: BillingInvoiceFiltersProps) => {
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const { startDate, endDate, projectId, status } = values;

  const statusSelectOptions = useMemo(
    () =>
      statusOptions.map(option => ({
        value: option,
        label: option,
      })),
    [statusOptions]
  );

  const { data: projectOptions = [], isLoading: projectLoading } = useQuery({
    queryKey: ["open-project-list"],
    queryFn: () => PtsProjectService.getOpenProjectList(),
  });

  const projectSelectOptions = useMemo(
    () =>
      projectOptions.map(option => ({
        value: option.value,
        label: option.label,
      })),
    [projectOptions]
  );

  const handleResetFilters = () => {
    onChange({
      startDate: "",
      endDate: "",
      projectId: undefined,
      status: undefined,
    });
  };

  return (
    <Collapsible open={filtersExpanded} onOpenChange={setFiltersExpanded}>
      <Card className="border border-gray-200 bg-white shadow-sm">
        <CardContent className="px-4 py-2">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="hover:bg-muted/40 flex w-full items-center justify-between rounded-md text-left transition-colors"
              aria-expanded={filtersExpanded}
              aria-label="Toggle filters"
            >
              <div className="flex items-center gap-2 py-1">
                <SlidersHorizontal className="text-muted-foreground h-4 w-4" />
                <span className="text-sm font-semibold text-gray-800">
                  Filters
                </span>
              </div>
              {filtersExpanded ? (
                <ChevronDown className="text-muted-foreground h-4 w-4 shrink-0" />
              ) : (
                <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
              )}
            </button>
          </CollapsibleTrigger>

          <CollapsibleContent className="border-t border-gray-100 pb-2 pt-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
              <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:flex-1 lg:grid-cols-4">
                <div className="flex w-full min-w-0 flex-col gap-1.5">
                  <Label
                    htmlFor={`${idPrefix}StartDate`}
                    className="text-xs font-medium text-gray-700"
                  >
                    Start Date
                  </Label>
                  <div className="relative w-full">
                    <input
                      type="date"
                      id={`${idPrefix}StartDate`}
                      value={startDate}
                      onChange={event =>
                        onChange({ ...values, startDate: event.target.value })
                      }
                      className={`${FILTER_DATE_INPUT_CLASS} pr-8`}
                    />
                    {startDate ? (
                      <button
                        type="button"
                        onClick={() =>
                          onChange({
                            ...values,
                            startDate: "",
                          })
                        }
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        title="Clear date"
                      >
                        <X size={14} />
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="flex w-full min-w-0 flex-col gap-1.5">
                  <Label
                    htmlFor={`${idPrefix}EndDate`}
                    className="text-xs font-medium text-gray-700"
                  >
                    End Date
                  </Label>
                  <div className="relative w-full">
                    <input
                      type="date"
                      id={`${idPrefix}EndDate`}
                      value={endDate}
                      onChange={event =>
                        onChange({ ...values, endDate: event.target.value })
                      }
                      className={`${FILTER_DATE_INPUT_CLASS} pr-8`}
                    />
                    {endDate ? (
                      <button
                        type="button"
                        onClick={() =>
                          onChange({
                            ...values,
                            endDate: "",
                          })
                        }
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        title="Clear date"
                      >
                        <X size={14} />
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="flex w-full min-w-0 flex-col gap-1.5">
                  <Label className="text-xs font-medium text-gray-700">
                    Project
                  </Label>
                  <div className="w-full min-w-0">
                    <Select
                      allowClear
                      placeholder={
                        projectLoading ? "Loading projects..." : "All projects"
                      }
                      value={projectId ?? undefined}
                      onChange={value =>
                        onChange({
                          ...values,
                          projectId: value || undefined,
                        })
                      }
                      className={FILTER_SELECT_CLASS}
                      style={{ width: "100%" }}
                      showSearch
                      loading={projectLoading}
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        String(option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={projectSelectOptions}
                      notFoundContent="No projects found"
                      listHeight={280}
                      popupMatchSelectWidth
                      getPopupContainer={trigger =>
                        trigger.parentElement ?? document.body
                      }
                    />
                  </div>
                </div>

                <div className="flex w-full min-w-0 flex-col gap-1.5">
                  <Label className="text-xs font-medium text-gray-700">
                    Status
                  </Label>
                  <div className="w-full min-w-0">
                    <Select
                      allowClear
                      placeholder="All statuses"
                      value={status ?? undefined}
                      onChange={value =>
                        onChange({
                          ...values,
                          status: value || undefined,
                        })
                      }
                      className={FILTER_SELECT_CLASS}
                      style={{ width: "100%" }}
                      options={statusSelectOptions}
                      listHeight={280}
                      popupMatchSelectWidth
                      getPopupContainer={trigger =>
                        trigger.parentElement ?? document.body
                      }
                    />
                  </div>
                </div>
              </div>

              <Button
                variant="outline"
                size="sm"
                className="h-9 w-full shrink-0 lg:w-[110px]"
                onClick={handleResetFilters}
              >
                <RotateCcw className="mr-1.5 h-4 w-4" />
                Reset
              </Button>
            </div>
          </CollapsibleContent>
        </CardContent>
      </Card>
    </Collapsible>
  );
};

export default BillingInvoiceFilters;
